import type { AppView } from '../types/views';

export type HelpCategory =
  | 'Bookings'
  | 'Clients'
  | 'Certificates'
  | 'Quotes'
  | 'Emails'
  | 'Reminders'
  | 'Search'
  | 'General';

export interface HelpGuide {
  id: string;
  title: string;
  situation: string;
  category: HelpCategory;
  steps: string[];
  tips?: string[];
  keywords: string[];
  contexts: string[];
  relatedFaqId?: string;
}

export interface HelpContextInput {
  surface?: string;
  tags?: string[];
}

export const helpGuideCategories: HelpCategory[] = [
  'Bookings',
  'Clients',
  'Certificates',
  'Quotes',
  'Emails',
  'Reminders',
  'Search',
  'General',
];

export const helpGuides: HelpGuide[] = [
  {
    id: 'company-booking-paste',
    title: 'Book a whole company (paste list)',
    situation:
      'You have a list of participant names and contacts from one company and need to add them all to a session roster at once.',
    category: 'Bookings',
    steps: [
      'Open **Bookings** and select the session (or open it from the calendar).',
      'Go to the **Roster** tab and click **Company booking**.',
      'Select the company, set total seats, course fee, and payment status.',
      'Under **Paste participants**, enter one person per line.',
      'Click **Parse list** (or wait — it also parses as you type).',
      'Review the preview: CRM matches, new records to create, or **reserved seats** for unnamed lines.',
      'Use **Invoice paid (all seats in group)** if the whole group is settled in one invoice.',
      'Click **Create booking** to add everyone to the roster.',
    ],
    tips: [
      'Format examples: `John Smith, john@example.com, 0871234567` or just `Maria Doyle`.',
      'Auto-match needs the same first name, surname, and email or phone — a shared company inbox alone is not enough.',
      'Unnamed lines become reserved seats until you fill in details later.',
    ],
    keywords: ['company booking', 'paste', 'parse', 'bulk', 'participants', 'roster', 'invoice paid'],
    contexts: ['session-roster', 'bookings', 'company-booking', 'roster'],
    relatedFaqId: 'session-company-booking',
  },
  {
    id: 'add-existing-client-session',
    title: 'Add one existing client to a session',
    situation: 'The person is already in the CRM and you want to enrol them on a course session.',
    category: 'Bookings',
    steps: [
      'Open **Bookings** and select the session.',
      'Go to the **Roster** tab and click **Add existing client**.',
      'Search the CRM by name, email, phone, or Sage code.',
      'Pick the correct match and set payment status.',
      'Confirm to add them to the roster.',
    ],
    keywords: ['add existing client', 'enrol', 'enroll', 'roster', 'session', 'search crm'],
    contexts: ['session-roster', 'bookings', 'roster'],
    relatedFaqId: 'session-add-client-to-course',
  },
  {
    id: 'create-client-and-enrol',
    title: 'Create a new client and enrol on a session',
    situation: 'The person is not in the CRM yet and you want to create their record and book them in one step.',
    category: 'Bookings',
    steps: [
      'Open **Bookings** and select the session.',
      'Go to the **Roster** tab and click **Create new client**.',
      'Fill in name, contact details, and payment.',
      'Save — they are created in the CRM and added to the roster.',
    ],
    keywords: ['create new client', 'new client', 'roster', 'session', 'enrol'],
    contexts: ['session-roster', 'bookings', 'clients', 'roster'],
    relatedFaqId: 'session-add-client-to-course',
  },
  {
    id: 'add-to-course-from-client',
    title: 'Add a client to a course from their record',
    situation: 'You are viewing a client in the CRM and want to book them onto an upcoming session.',
    category: 'Clients',
    steps: [
      'Open **Clients** and select the client.',
      'Click **Add to course**.',
      'Pick the session, set payment, and confirm.',
      'If the session is full, they can be placed on the waiting list instead.',
    ],
    keywords: ['add to course', 'client record', 'enrol', 'session'],
    contexts: ['clients-detail', 'clients'],
    relatedFaqId: 'session-add-client-to-course',
  },
  {
    id: 'create-new-session',
    title: 'Create a new course session',
    situation: 'You need to schedule a new training date with capacity, venue, and instructor.',
    category: 'Bookings',
    steps: [
      'Go to **Bookings** and click **New session**.',
      'Enter course name, date(s), capacity, location, instructor, language, and delivery mode.',
      'Save the session.',
      'Add attendees from the **Roster** tab (**Add existing client**, **Create new client**, or **Company booking**).',
    ],
    keywords: ['new session', 'create session', 'schedule', 'bookings', 'capacity'],
    contexts: ['bookings'],
    relatedFaqId: 'booking-create',
  },
  {
    id: 'waiting-list',
    title: 'Session is full — use the waiting list',
    situation: 'All seats are taken but you still need to hold a place for someone until a seat opens.',
    category: 'Bookings',
    steps: [
      'Open the session **Roster** tab.',
      'Add or move the attendee using **Move to waiting list**.',
      'When a seat opens, promote them from the waiting list back to **booked**.',
      'Quote acceptances may land on the waiting list until payment is recorded.',
    ],
    keywords: ['waiting list', 'full', 'promote', 'seat', 'roster'],
    contexts: ['session-roster', 'bookings', 'roster'],
    relatedFaqId: 'session-waiting-list',
  },
  {
    id: 'per-seat-payments',
    title: 'Record payments on the roster',
    situation: 'You need to mark individual seats or a whole company group as paid, partial, or unpaid.',
    category: 'Bookings',
    steps: [
      'Open the session **Roster** tab.',
      'Each attendee has a payment status: unpaid, partial, or paid.',
      'Edit an attendee to record payment when adding or updating them.',
      'For company bookings, use **Invoice paid (all seats in group)** to settle every seat at once.',
    ],
    keywords: ['payment', 'per-seat', 'invoice paid', 'unpaid', 'partial', 'roster'],
    contexts: ['session-roster', 'bookings', 'roster', 'company-booking'],
    relatedFaqId: 'session-payments',
  },
  {
    id: 'sage-import',
    title: 'Import clients from Sage Contacts Report',
    situation: 'You have a Sage Contacts Report export (.xls / .xlsx) and want to bulk-import companies into the CRM.',
    category: 'Clients',
    steps: [
      'Go to **Clients** and open **Import**.',
      'Choose **Sage Contacts Report**.',
      'Drop or select your Sage export file (.xls or .xlsx).',
      'Review the preview: rows to import, duplicates skipped, and any warnings.',
      'Click **Import** to create clients with Sage codes, addresses, phones, emails, and contacts.',
    ],
    tips: [
      'Existing clients matched by Sage code or company name are skipped automatically.',
      'Sage codes appear in the clients list and can be searched with Ctrl+K.',
    ],
    keywords: ['sage', 'import', 'contacts report', 'excel', 'xls', 'bulk', 'ireland tt'],
    contexts: ['clients', 'clients-import', 'sage-import'],
  },
  {
    id: 'csv-import-clients',
    title: 'Import clients via CSV',
    situation: 'You have a spreadsheet of companies or locations to add or update in the CRM.',
    category: 'Clients',
    steps: [
      'Go to **Clients** and open **Import**.',
      'Choose **Add to Existing Clients** (locations/contacts for known companies) or **Import New Clients** (new companies).',
      'Download the matching template if you need the correct column layout.',
      'Upload your CSV and follow the preview steps.',
    ],
    keywords: ['csv', 'import', 'bulk', 'template', 'locations', 'correlation'],
    contexts: ['clients', 'clients-import'],
    relatedFaqId: 'client-import',
  },
  {
    id: 'search-sage-code',
    title: 'Find a client by Sage code',
    situation: 'You know the Sage account code (e.g. 1CREATWOOD) and want to jump to that client quickly.',
    category: 'Search',
    steps: [
      'Press **Ctrl+K** (or use the search bar) for global search.',
      'Type the Sage code — partial matches work.',
      'Select the client from the results; the code also appears in the **Sage** column on the clients list.',
      'On the clients page you can search by Sage code in the list search box too.',
    ],
    keywords: ['sage code', 'search', 'ctrl+k', 'global search', 'find client'],
    contexts: ['clients', 'clients-detail', 'global'],
  },
  {
    id: 'upload-certificates',
    title: 'Upload and generate certificates',
    situation: 'You have attendee data in Excel or CSV and need to produce certificate PDFs.',
    category: 'Certificates',
    steps: [
      'Go to **Certificates** (main upload area).',
      'Upload your Excel (.xlsx, .xls) or CSV file.',
      'Ensure columns include Attendee Name, Company Name, Course Name, and Course Date.',
      'Process the file, then generate or download certificates individually or in bulk.',
    ],
    tips: [
      'Certificates count toward payment cycles only when actually generated and downloaded.',
    ],
    keywords: ['certificate', 'upload', 'excel', 'csv', 'generate', 'pdf'],
    contexts: ['certificates'],
    relatedFaqId: 'cert-upload',
  },
  {
    id: 'cancel-restore-session',
    title: 'Cancel or restore a course session',
    situation: 'A session is cancelled or you need to reopen one that was cancelled earlier.',
    category: 'Bookings',
    steps: [
      'Open the session in **Bookings**.',
      'Open the **Manage** menu and choose **Cancel session**.',
      'Enter a cancellation reason (required).',
      'The session stays on the calendar in red and blocks new enrolments.',
      'To reopen later: **Manage** → **Restore session**.',
    ],
    keywords: ['cancel session', 'restore', 'cancelled', 'manage'],
    contexts: ['session-roster', 'bookings'],
    relatedFaqId: 'session-cancel-restore',
  },
  {
    id: 'session-tabs-overview',
    title: 'What the session tabs are for',
    situation: 'You opened a session and want to know which tab to use for what.',
    category: 'Bookings',
    steps: [
      '**Roster** — add attendees, payments, waiting list, company booking.',
      '**Spreadsheet** — bulk table view of roster data.',
      '**Post-session** — attendance and certificate follow-up.',
      '**Logistics** — venue, instructor, dates, delivery details.',
      '**Interpreters** / **Expenses** / **History** — support and audit trails.',
    ],
    tips: ['Day-to-day enrolment happens on the **Roster** tab.'],
    keywords: ['tabs', 'roster', 'logistics', 'post-session', 'spreadsheet', 'workspace'],
    contexts: ['session-roster', 'bookings', 'roster', 'logistics', 'postSession'],
    relatedFaqId: 'session-roster-tabs',
  },
  {
    id: 'assign-instructor',
    title: 'Assign an instructor to a session',
    situation: 'You need to put the right trainer on a course date.',
    category: 'Bookings',
    steps: [
      'Open the session in **Bookings**.',
      'Go to the **Logistics** tab.',
      'Select the instructor and save.',
      'Send weekly assignment summaries from **Reminders** → **Instructors** if needed.',
    ],
    keywords: ['instructor', 'assign', 'logistics', 'trainer'],
    contexts: ['session-roster', 'bookings', 'logistics'],
    relatedFaqId: 'booking-instructor',
  },
  {
    id: 'mark-no-show',
    title: 'Mark an attendee as no-show or cancelled',
    situation: 'Someone did not attend or pulled out and you need to update the roster.',
    category: 'Bookings',
    steps: [
      'Open the session **Roster** tab.',
      'Find the attendee and open their row actions.',
      'Choose **no-show** or **cancelled** as needed.',
      'If a seat opens, promote someone from the waiting list.',
    ],
    keywords: ['no show', 'cancelled', 'attendee status', 'roster'],
    contexts: ['session-roster', 'bookings', 'roster'],
    relatedFaqId: 'booking-statuses',
  },
  {
    id: 'ops-day-planner',
    title: 'Use the Ops Day Planner status board',
    situation: 'You want a Kanban-style view of legacy booking workflow for the day.',
    category: 'Bookings',
    steps: [
      'Open **Ops** from the sidebar.',
      'Use the **Status Board** columns (Scheduled → Confirmed → Reminder Sent → Certs Produced → Job Paid → Completed).',
      'Drag cards between columns to update status.',
      'Use main **Bookings** for session cards/tables; Ops is for legacy booking workflow.',
    ],
    keywords: ['ops', 'status board', 'kanban', 'day planner'],
    contexts: ['ops', 'bookings'],
    relatedFaqId: 'ops-day-planner',
  },
  {
    id: 'add-new-client',
    title: 'Add a new client manually',
    situation: 'You need to create a company or individual in the CRM without importing.',
    category: 'Clients',
    steps: [
      'Go to **Clients** and click **Add New Client**.',
      'Enter company or person details (name is required).',
      'Save, then add **Locations** and **Contacts** on the client record.',
    ],
    keywords: ['add client', 'new client', 'create client', 'crm'],
    contexts: ['clients'],
    relatedFaqId: 'client-add',
  },
  {
    id: 'add-client-location',
    title: 'Add a location to a client',
    situation: 'A company has a site address you need on file for bookings or emails.',
    category: 'Clients',
    steps: [
      'Open the client in **Clients**.',
      'Scroll to **Locations** and click **Add Location**.',
      'Fill in address lines, city, county, and site contacts if needed.',
      'Save — you can add multiple sites per company.',
    ],
    keywords: ['location', 'address', 'site', 'add location'],
    contexts: ['clients-detail', 'clients'],
    relatedFaqId: 'client-locations',
  },
  {
    id: 'add-client-contact',
    title: 'Add a contact person to a client',
    situation: 'You need a billing or general contact for emails and reminders.',
    category: 'Clients',
    steps: [
      'Open the client in **Clients**.',
      'Go to **Contacts** and click **Add Contact**.',
      'Enter name, email, phone, and whether they are billing / general.',
      'Save — contacts are used for booking emails and reminders.',
    ],
    keywords: ['contact', 'billing contact', 'email', 'add contact'],
    contexts: ['clients-detail', 'clients'],
    relatedFaqId: 'client-contacts',
  },
  {
    id: 'pin-client-notes',
    title: 'Pin important client notes',
    situation: 'A note must stay at the top of the client history (access codes, special requirements, etc.).',
    category: 'Clients',
    steps: [
      'Open the client detail page.',
      'Find the note under **Company Notes & Email History**.',
      'Click the **pin** icon next to the note.',
      'Pinned notes stay at the top; click pin again to unpin.',
    ],
    keywords: ['pin', 'notes', 'important', 'client notes'],
    contexts: ['clients-detail', 'clients'],
    relatedFaqId: 'client-notes-pinning',
  },
  {
    id: 'client-portal-link',
    title: 'Share a client portal link',
    situation: 'The client needs to view or download their own certificates securely.',
    category: 'Clients',
    steps: [
      'Open the client record in **Clients**.',
      'Generate or copy the **portal link** for that client.',
      'Send the secure link to the client — they only see their own data.',
    ],
    keywords: ['portal', 'client portal', 'certificates', 'share link'],
    contexts: ['clients-detail', 'clients'],
    relatedFaqId: 'client-portal',
  },
  {
    id: 'generate-certs-from-session',
    title: 'Generate certificates after a session',
    situation: 'Training is done and you need to issue certificates from the session roster.',
    category: 'Certificates',
    steps: [
      'Open the session in **Bookings**.',
      'Go to the **Post-session** tab (or roster certificate actions where available).',
      'Confirm attendance for each person who completed the course.',
      'Generate / download certificates for eligible attendees.',
    ],
    tips: [
      'Certificates usually require the attendee to be marked attended.',
      'Reminders are created when certificates are actually generated and downloaded.',
    ],
    keywords: ['post-session', 'generate certificate', 'attendance', 'roster'],
    contexts: ['session-roster', 'bookings', 'certificates', 'postSession'],
    relatedFaqId: 'cert-download',
  },
  {
    id: 'revoke-certificate',
    title: 'Revoke or remove a certificate',
    situation: 'A certificate was issued in error or must be invalidated.',
    category: 'Certificates',
    steps: [
      'Open the client → **Generated Certificates**.',
      '**Revoke** marks it invalid but keeps the record (shows gray).',
      '**Remove** deletes the certificate record permanently.',
      'Use admin code when the system asks for sensitive confirmations.',
    ],
    keywords: ['revoke', 'remove', 'invalid', 'certificate status'],
    contexts: ['certificates', 'clients-detail'],
    relatedFaqId: 'cert-status-management',
  },
  {
    id: 'create-send-quote',
    title: 'Create and send a quote',
    situation: 'A client needs a price for a course before you book them.',
    category: 'Quotes',
    steps: [
      'Go to **Quotes** and create a new quote.',
      'Add course line items, pricing, and validity dates.',
      'Optionally **link the quote to a scheduled session**.',
      'Send the public acceptance link to the client.',
      'When they accept and payment is recorded, seats can move from waiting list onto the roster.',
    ],
    keywords: ['quote', 'pricing', 'acceptance', 'send quote'],
    contexts: ['quotes', 'bookings'],
    relatedFaqId: 'quote-system',
  },
  {
    id: 'send-booking-email',
    title: 'Send a booking confirmation email',
    situation: 'You need to email a client or instructor about a booking.',
    category: 'Emails',
    steps: [
      'Open the client or session booking flow.',
      'Choose **Generate booking email** / the email action for that booking.',
      'Pick a template and the contact recipient.',
      'Preview, then send — placeholders fill company, course, dates automatically.',
    ],
    keywords: ['booking email', 'confirmation', 'template', 'send email'],
    contexts: ['bookings', 'clients-detail', 'emails'],
    relatedFaqId: 'email-booking',
  },
  {
    id: 'email-outbox',
    title: 'Check queued and sent emails',
    situation: 'You need to see if an email went out, is still queued, or failed.',
    category: 'Emails',
    steps: [
      'Go to **Emails** in the sidebar.',
      'Review pending, sent, and failed items.',
      'Check recipient, timestamp, and status for the message you care about.',
    ],
    keywords: ['outbox', 'queued', 'sent', 'failed', 'delivery log'],
    contexts: ['emails'],
    relatedFaqId: 'email-outbox',
  },
  {
    id: 'bulk-email-campaign',
    title: 'Run a bulk email campaign',
    situation: 'You need to email many clients at once with a template.',
    category: 'Emails',
    steps: [
      'Open **Emails** / bulk campaign tools.',
      'Choose or create an email template with placeholders.',
      'Select recipients (filters / client list).',
      'Attach files if needed, preview, then send.',
      'Monitor delivery in the email outbox.',
    ],
    keywords: ['bulk email', 'campaign', 'template', 'recipients'],
    contexts: ['emails'],
    relatedFaqId: 'bulk-email-workflow',
  },
  {
    id: 'send-expiry-reminders',
    title: 'Send certificate expiry reminders',
    situation: 'Certificates are due for renewal and you need to notify clients.',
    category: 'Reminders',
    steps: [
      'Go to **Reminders**.',
      'Filter by expiring soon / overdue if needed.',
      'Click the email icon next to a reminder or company.',
      'Pick a template and contact, then send.',
      'Status shows **Queued** or **Sent** from the email outbox.',
    ],
    tips: [
      'Reminders are created when certificates are generated and downloaded — not only uploaded.',
    ],
    keywords: ['reminder', 'expiry', 'renewal', 'send email'],
    contexts: ['reminders'],
    relatedFaqId: 'reminder-email',
  },
  {
    id: 'mark-certificate-renewed',
    title: 'Mark a certificate as renewed',
    situation: 'The client has completed renewal training and you need to clear the reminder.',
    category: 'Reminders',
    steps: [
      'Open **Reminders**.',
      'Find the certificate and check **Renewed**.',
      'The renewal date is recorded and the item moves to history.',
      'To undo, enter the admin code when prompted.',
    ],
    keywords: ['renewed', 'mark renewed', 'admin code', 'reminder'],
    contexts: ['reminders'],
    relatedFaqId: 'reminder-renewed',
  },
  {
    id: 'send-review-request',
    title: 'Send a Google review request',
    situation: 'After training you want to ask the client for a review.',
    category: 'Reminders',
    steps: [
      'Go to **Reviews** (or review reminders).',
      'Open the review request for the client.',
      'Ensure the Google review link is set up in settings.',
      'Send the review email using a template.',
    ],
    keywords: ['review', 'google review', 'feedback'],
    contexts: ['reviews', 'reminders'],
    relatedFaqId: 'review-email',
  },
  {
    id: 'global-search',
    title: 'Search anything with Ctrl+K',
    situation: 'You need to jump to a client, session, booking, quote, or instructor quickly.',
    category: 'Search',
    steps: [
      'Press **Ctrl+K** (or click the top search bar).',
      'Type a name, email, phone, Sage code, or training reference.',
      'Pick a result — clients, sessions, bookings, quotes, and more appear in sections.',
    ],
    tips: ['Sage codes are searchable too (e.g. `1CREATWOOD`).'],
    keywords: ['global search', 'ctrl+k', 'find', 'search'],
    contexts: ['global', 'clients', 'bookings'],
    relatedFaqId: 'advanced-search',
  },
  {
    id: 'qr-checkin',
    title: 'Use QR check-in on site',
    situation: 'Attendees need to submit details quickly at the venue.',
    category: 'General',
    steps: [
      'Have the instructor open the attendance / QR check-in view.',
      'Display the QR code for attendees to scan.',
      'They open the public form and submit their details.',
      'Review submissions before generating certificates.',
    ],
    keywords: ['qr', 'check-in', 'attendance', 'public form'],
    contexts: ['bookings', 'session-roster'],
    relatedFaqId: 'qr-checkin',
  },
];

/** Fallback when no context-specific guides match */
export const popularGuideIds = [
  'company-booking-paste',
  'add-existing-client-session',
  'sage-import',
  'add-to-course-from-client',
  'create-new-session',
];

const VIEW_CONTEXT_MAP: Partial<Record<AppView, string[]>> = {
  bookings: ['bookings'],
  clients: ['clients'],
  certificates: ['certificates'],
  dashboard: ['global'],
  quotes: ['quotes', 'bookings'],
  ops: ['ops', 'bookings'],
  reminders: ['reminders'],
  reviews: ['reviews', 'reminders'],
  emails: ['emails'],
};

function guideById(id: string): HelpGuide | undefined {
  return helpGuides.find((g) => g.id === id);
}

function scoreGuide(guide: HelpGuide, viewContexts: string[], surface?: string, tags: string[] = []): number {
  let score = 0;
  const allContexts = [...viewContexts, ...(surface ? [surface] : []), ...tags];

  for (const ctx of allContexts) {
    if (guide.contexts.includes(ctx)) {
      score += surface && guide.contexts.includes(surface) ? 40 : 20;
    }
  }

  if (tags.includes('roster') && guide.contexts.includes('roster')) score += 30;
  if (tags.includes('sage-import') && guide.id === 'sage-import') score += 100;
  if (tags.includes('company-booking') && guide.id === 'company-booking-paste') score += 80;

  return score;
}

export function getGuidesForContext(
  activeView: AppView,
  helpContext?: HelpContextInput,
  limit = 5
): HelpGuide[] {
  const viewContexts = VIEW_CONTEXT_MAP[activeView] ?? [];
  const surface = helpContext?.surface;
  const tags = helpContext?.tags ?? [];

  const scored = helpGuides
    .map((guide) => ({
      guide,
      score: scoreGuide(guide, viewContexts, surface, tags),
    }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score);

  if (scored.length === 0) {
    return popularGuideIds.map((id) => guideById(id)).filter(Boolean) as HelpGuide[];
  }

  const seen = new Set<string>();
  const result: HelpGuide[] = [];
  for (const { guide } of scored) {
    if (seen.has(guide.id)) continue;
    seen.add(guide.id);
    result.push(guide);
    if (result.length >= limit) break;
  }
  return result;
}

export function getGuidesByCategory(category: HelpCategory): HelpGuide[] {
  return helpGuides.filter((g) => g.category === category);
}

export function getGuideById(id: string): HelpGuide | undefined {
  return guideById(id);
}

export function searchGuides(query: string): HelpGuide[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return helpGuides;

  const words = normalized.split(/\s+/).filter((w) => w.length >= 2);

  return helpGuides
    .map((guide) => {
      const haystack = [
        guide.title,
        guide.situation,
        guide.category,
        ...guide.steps,
        ...(guide.tips ?? []),
        ...guide.keywords,
      ]
        .join(' ')
        .toLowerCase();

      let score = 0;
      if (haystack.includes(normalized)) score += 100;
      if (guide.title.toLowerCase().includes(normalized)) score += 80;
      words.forEach((word) => {
        if (guide.keywords.some((k) => k.includes(word))) score += 30;
        if (haystack.includes(word)) score += 15;
      });
      return { guide, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((entry) => entry.guide);
}
